// ─────────────────────────────────────────────────────────────────────────────
// targetGeometry — single source of truth for target face render geometry.
//
// The pixel→radius layer in lib/scoring.js (hitZone) MUST mirror the SVG
// geometry rendered by components/Target.jsx. Historically the two kept their
// own copies of the spot centres, spot radii, ring outer radii, and the 3D
// vital centre, synchronised only by "kept in sync" comments — a render-geometry
// change could silently mis-score a tapped arrow (issue #1368 / H6).
//
// This module is imported by BOTH scoring.js and Target.jsx so the geometry
// cannot drift. All coordinates are in SVG viewbox units: the canvas is
// 0–240 on each axis with the face centred at (120, 120).
//
// Values below are re-pointed to shared/face-definitions.json (issue #1680
// PR 2) — the single source of truth also consumed by the backend
// (backend/app/face_definitions.py). Export names are unchanged so
// Target.jsx and scoring.js need no changes.
// ─────────────────────────────────────────────────────────────────────────────

import faceDefinitions from "../../../shared/face-definitions.json";

const { faceTypes, faces } = faceDefinitions;

// [x, y] pair (as stored in the JSON) -> { x, y } point.
const toPoint = ([x, y]) => ({ x, y });
const spotCenters = (faceCode) => faces[faceCode].spot_centers.map(toPoint);

// Viewbox geometry — every face renders into a 240×240 viewbox centred here.
export const VIEWBOX = 240;
export const CENTER = { x: 120, y: 120 };

// Half the viewbox — also the scale factor for normalised arrow coordinates,
// which run ±1.0 with 1.0 at the paper edge. Overlay components had this
// baked in as the literal `120 + a.x * 120` nine times over (#2068).
export const VIEWBOX_HALF = VIEWBOX / 2;

/** Normalised arrow x (±1, 1.0 = paper edge) → viewbox x (0–240). */
export const toVbX = (x) => CENTER.x + (x ?? 0) * VIEWBOX_HALF;

/** Normalised arrow y (±1, 1.0 = paper edge) → viewbox y (0–240). */
export const toVbY = (y) => CENTER.y + (y ?? 0) * VIEWBOX_HALF;

/** Normalised radius/semi-axis (±1) → viewbox units. No centre offset. */
export const toVbLen = (r) => (r ?? 0) * VIEWBOX_HALF;

// WA 10-ring face: outer scoring ring radius (a 20-unit miss margin clears the
// viewBox edge). Used by both WARings and hitZone's WA conversion.
export const WA_OUTER_R = faceTypes.wa_full.outer_r;

// Vegas triple-spot centres — 70 apart vertically — and the per-spot radius
// (30, leaving a 10-unit gap between adjacent spots).
export const VEGAS_SPOT_CENTERS = spotCenters("vegas");
export const VEGAS_SPOT_R = faces.vegas.spot_outer_r;

// WA triangular triple-spot centres — two spots low, one high — and per-spot
// radius. Same per-spot scoring as the Vegas face.
export const TRI_SPOT_CENTERS = spotCenters("3spot-tri");
export const TRI_SPOT_R = faces["3spot-tri"].spot_outer_r;

// NFAA 5-spot quincunx centres and per-spot radius.
export const NFAA5_SPOT_CENTERS = spotCenters("nfaa5spot");
export const NFAA5_SPOT_R = faces.nfaa5spot.spot_outer_r;

// NFAA indoor single-spot: outer scoring ring radius (reference: 201.8 × 0.48).
export const NFAA_SINGLE_OUTER_R = faceTypes.nfaa_single.outer_r;

// WA Field 3-spot: 3 × 20 cm spots stacked vertically (mirrors the Vegas
// layout) and the per-spot outer scoring radius — 33 fits three spots at
// y = 50/120/190 without overlap. Mirrored by _SPOT_CENTERS / _SPOT_RADII in
// backend/app/scoring.py (#1667).
export const WAFIELD3_SPOT_CENTERS = spotCenters("wafield3spot");
export const WAFIELD3_SPOT_R = faces.wafield3spot.spot_outer_r;

// 3D animal vital-zone centre and outer radius. The concentric scoring rings
// (12/11/10/8/5) are centred here; shared by the 3D / IBO / 2-arrow faces.
// The vital-centre y-offset (130, vs the 120 viewbox centre) has no shared-JSON
// field — single-spot faces are implicitly centred at CENTER except this one —
// so it stays a literal here.
export const D3_VITAL = { x: 120, y: 130 };
export const D3_OUTER_R = faceTypes.asa_3d.outer_r;
